// Imports
import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import uniqueId from 'lodash/uniqueId';
import styled, { ThemeProvider } from 'styled-components/macro';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import ControlGroup from '../../../../../../shareables/foundation/front-end/components/forms/control-group';
import InputContainer from '../../../../../../shareables/foundation/front-end/components/forms/input-container';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import BaseFieldProps from './base-field-props';
import isScreenshot from '../../../../../utils/is-screenshot';
import passwordManagerDisablingProps from './password-manager-disabling-props';


// Styled component
const BottomPortion = styled.div`
	display: grid;
	justify-content: space-between;
	grid-template-columns: auto 1fr;
	gap: 1.5rem;
`;

const CharacterCount = styled.span`
	line-height: 1;
	grid-area: 1 / 2 / 2 / 3;
	justify-self: end;
	white-space: nowrap;
`;


// Define the accepted props
interface Props extends BaseFieldProps {
	/** The number of rows tall the `<textarea />` element should be. */
	rows: number;
	
	
	/** The initial value state of the field. Will be ignored on subsequent renders. */
	initialValue?: string;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newValue: string) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => string) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (value: string) => void) => void;
	
	
	/** Whether to display the character count beneath the field. Defaults to `false`. */
	displayCharacterCount?: true;
	
	
	/** Whether to disable password managers or not. Defaults to `false`. */
	disablePasswordManagers?: boolean;
}

type CompleteProps = Props &
	Omit<
		React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
		'id' | 'className' | 'value' | 'defaultValue' | 'defaultChecked' | 'prefix' | 'rows' | 'onChange'
	>;


// Function component
const Textarea = React.forwardRef<HTMLTextAreaElement, CompleteProps>((props: CompleteProps, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		initialValue,
		label,
		note,
		errorMessage: errorMessageProp,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude: excludeProp,
		displayCharacterCount,
		disablePasswordManagers,
		...passThroughProps
	} = props;
	
	
	// Catch use of character count without defining a max length
	if (displayCharacterCount && !passThroughProps.maxLength) {
		throw new Error('For character count to be displayed, a max length must be given');
	}
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === passThroughProps.name ? context.error.message : errorMessageProp;
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? passThroughProps.disabled ?? undefined;
	
	
	// Use state
	const [value, setValue] = useState<string>(initialValue || '');
	
	
	// Bind getter/setter
	bindGetter && bindGetter(() => value);
	bindSetter && bindSetter(setValue);
	
	
	// Add and remove getter from connected form
	const { name } = passThroughProps;
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, () => value);
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, value, addValueGetter, removeValueGetter, exclude]);
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('textarea-field-'));
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
		setValue(event.target.value);
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		recoverState &&
			recoverState.enabled &&
			debouncedSaveValue(passThroughProps.name, value, recoverState.uniqueIdentifier);
	}, [value]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(value);
		context?.onChange?.(true);
	}, [value]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Attempt to recover state on mount
	if (recoverState && recoverState.enabled && hasMounted.current === false) {
		hasMounted.current = true;
		
		window.setTimeout(() => {
			const recoveredValue = parsePersistentFormValue(recoverState.uniqueIdentifier, name, z.string());
			
			if (typeof recoveredValue !== 'undefined') {
				setValue(recoveredValue);
			}
		}, 1); // Must happen asynchronously
		
		/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
	}
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Modify textarea display style if character count is displayed
	if (displayCharacterCount) {
		passThroughProps.style = { ...passThroughProps.style, display: 'block' };
	}
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer>
				{label && (
					<Label htmlFor={uniqueID.current}>
						{label}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				<ControlGroup>
					<InputContainer>
						<textarea
							{...passThroughProps}
							id={uniqueID.current}
							value={value}
							onChange={handleChange}
							ref={ref}
							aria-invalid={Boolean(errorMessage)}
							aria-describedby={`error-field-${uniqueID.current}`}
							{...(isScreenshot() ? { spellCheck: 'false' } : {})}
							{...(disablePasswordManagers ? passwordManagerDisablingProps : {})}
						/>
						
						<BottomPortion>
							{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
							
							{displayCharacterCount && (
								<CharacterCount>
									<small className='is-gray'>
										{value.length}/{passThroughProps.maxLength}
									</small>
								</CharacterCount>
							)}
						</BottomPortion>
					</InputContainer>
				</ControlGroup>
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
Textarea.displayName = 'Textarea';

export default Textarea;
